import { useMemo } from 'react';
import { UseFormReset } from 'react-hook-form';

import { mapNotesToManagedNotes } from 'bom/components/AddPartNote';
import { FormProvider, useNotificationContext } from 'core/states';
import { usePartBomItemActions } from 'partBoms/hooks';
import { IPartBom, IPartBomItem } from 'partBoms/types';
import { PartNumberHelper } from 'parts/helpers';

import { mapUpsertItemData } from './mappers';
import { upsertItemSchema } from './schema';
import { IUpsertItemFormData } from './types';
import { UpsertItemFormFields } from './UpsertItemFormFields';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  partBom: IPartBom;
  partBomItem?: IPartBomItem;
  onSubmitSuccess?: (partBomItem: IPartBomItem) => void;
}

export const UpsertItem = ({ isOpen, onClose, partBom, partBomItem, onSubmitSuccess }: IProps) => {
  const { clearNotification } = useNotificationContext();

  const { upsertPartBomItem } = usePartBomItemActions();

  const isEdit = useMemo(() => !!partBomItem, [partBomItem]);

  const defaultValues = useMemo(() => {
    if (!isEdit) {
      return {};
    }

    return {
      part: {
        ...partBomItem.partArr,
        number: PartNumberHelper.toLabel(partBomItem.partArr?.number),
        externalPartStatus: partBomItem.externalPartStatus,
      },
      manufacturer: partBomItem.partArr?.manufacturerId
        ? {
            id: partBomItem.partArr?.manufacturerId,
            name: partBomItem.partArr?.manufacturerName,
          }
        : null,
      description: partBomItem.partArr?.descriptionLong || partBomItem.partArr?.description,
      quantity: partBomItem.quantity,
      uom: partBomItem.uom,
      notes: mapNotesToManagedNotes(Object.values(partBomItem.notes || {})),
    };
  }, [isEdit, partBomItem]);

  const onSubmit =
    (reset: UseFormReset<IUpsertItemFormData>) => async (data: IUpsertItemFormData) => {
      const { success, partBomItem: updatedPartBomItem } = await upsertPartBomItem(
        mapUpsertItemData(data, partBomItem?.id, partBom?.id)
      );

      if (!success) {
        return;
      }

      setTimeout(() => {
        handleOnSubmitSuccess(reset, updatedPartBomItem);
      }, 1000);
    };

  const handleOnSubmitSuccess = (
    reset: UseFormReset<IUpsertItemFormData>,
    partBomItem: IPartBomItem
  ) => {
    handleOnClose(reset);
    onSubmitSuccess?.(partBomItem);
  };

  const handleOnClose = (reset?: UseFormReset<IUpsertItemFormData>) => {
    reset?.(defaultValues);

    onClose();
    clearNotification();
  };

  return (
    <FormProvider schema={upsertItemSchema()} defaultValues={defaultValues} resetDefaultValues>
      <UpsertItemFormFields
        isOpen={isOpen}
        onClose={handleOnClose}
        partBomItem={partBomItem}
        onSubmit={onSubmit}
      />
    </FormProvider>
  );
};
